// src/App.jsx
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import React, { Suspense } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry.js";
import ScrollManager from "./src/hooks/useScrollRestoration.js";

/* ✅ Globales Toast-System (gestylt) */
import ToastSystem from "./components/ui/ToastSystem.jsx";
import { PdfExportPreviewHost } from "./utils/pdfExportPreviewHost.jsx";

/* ✅ Confirm-Provider von dir */
import { ConfirmProvider } from "./components/ui/ConfirmationModal.jsx";
import useSessionActivity from "./constants/useSessionActivity.js";
import ErrorBoundary from "./components/ui/ErrorBoundary.jsx";
import AuthRequiredListener from "./components/ui/AuthRequiredListener.jsx";

/* ✅ Theme Provider — festes Dark-Theme */
import { ThemeProvider } from "./context/ThemeContext.jsx";
import "./styles/theme.css";

/* ✅ Radio Provider für globalen Radio-Player */
import { RadioProvider } from "./context/RadioContext.jsx";


/* Auth & Shell */
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import PrivateRoute from "./PrivateRoute";

/* Pflicht-Schritte */
import TwoFASetup from "./components/Login/TwoFASetup.jsx";
import FirstPassword from "./components/Login/FirstPasswort.jsx";
import ResetPassword from "./components/Login/ResetPassword.jsx";

/* Admin — lazy */
const AdminPage = lazyWithRetry(() => import("./pages/Admin.jsx"));
const AdminUserEditPage = lazyWithRetry(() => import("./components/admin/AdminUserEditPage.jsx"));

/* Archiv — lazy */
const Archiv = lazyWithRetry(() => import("./pages/Archiv"));

/* Offerten — lazy Listen/Details */
const OffertenListe = lazyWithRetry(() => import("./components/offerte/OffertenListe.jsx"));
const OfferteDetails = lazyWithRetry(() => import("./components/offerte/OfferteDetails.jsx"));
const OfferteNeu = lazyWithRetry(() => import("./components/offerte/OfferteNeu.jsx"));
const OfferteBearbeiten = lazyWithRetry(() => import("./components/offerte/OfferteBearbeiten.jsx"));

/* Sales */
const VerkaufHome = lazyWithRetry(() => import("./pages/verkauf/VerkaufHome.jsx"));
const VerkaufOffertenPage = lazyWithRetry(() => import("./pages/verkauf/VerkaufOffertenPage.jsx"));
const VerkaufOfferCreate = lazyWithRetry(() => import("./pages/verkauf/VerkaufOfferCreate.jsx"));
const VerkaufOfferWiretrackCreate = lazyWithRetry(() => import("./pages/verkauf/VerkaufOfferWiretrackCreate.jsx"));
const VerkaufInvoiceCreate = lazyWithRetry(() => import("./pages/verkauf/VerkaufInvoiceCreate.jsx"));
const VerkaufAiPage = lazyWithRetry(() => import("./pages/verkauf/VerkaufAiPage.jsx"));
const VerkaufKnowledgePage = lazyWithRetry(() => import("./pages/verkauf/VerkaufKnowledgePage.jsx"));
const VerkaufTemplatesPage = lazyWithRetry(() => import("./pages/verkauf/VerkaufTemplatesPage.jsx"));
const VerkaufSharepointPage = lazyWithRetry(() => import("./pages/verkauf/VerkaufSharepointPage.jsx"));
const SalesDocumentList = lazyWithRetry(() => import("./pages/verkauf/SalesDocumentList.jsx"));
const SalesDocumentEditor = lazyWithRetry(() => import("./pages/verkauf/SalesDocumentEditor.jsx"));
const DebitorenPage = lazyWithRetry(() => import("./pages/verkauf/DebitorenPage.jsx"));
const SalesSettingsPage = lazyWithRetry(() => import("./pages/verkauf/SalesSettingsPage.jsx"));

/* Kalender — lazy */
const Kalender = lazyWithRetry(() => import("./components/kalender/Kalender.jsx"));
const KalenderScreen = lazyWithRetry(() => import("./components/kalender/KalenderScreen.jsx"));

/* Kunden */
import KundenListe from "./components/projekteListen/KundenListe";
import KundenNeu from "./modules/kunden/pages/KundenCreatePage.jsx";
import KundenBearbeiten from "./modules/kunden/pages/KundenEditPage.jsx";
import KundenDetails from "./modules/kunden/pages/KundenDetailsPage.jsx";
import ObjektDetails from "./modules/objekte/pages/ObjektDetailsPage.jsx";

/* Projekte – Listen — lazy */
const ProjektElektroListe = lazyWithRetry(() => import("./modules/projekte/pages/ProjektElektroListePage.jsx"));
const ProjektePVListe = lazyWithRetry(() => import("./modules/projekte/pages/ProjektePVListePage.jsx"));
const ProjekteSicherheitListe = lazyWithRetry(() => import("./modules/projekte/pages/ProjekteSicherheitListePage.jsx"));
const ProjekteLadestationListe = lazyWithRetry(() => import("./modules/projekte/pages/ProjekteLadestationListePage.jsx"));
const ProjekteInternListe = lazyWithRetry(() => import("./modules/projekte/pages/ProjekteInternListePage.jsx"));
const ProjekteLadenbauListe = lazyWithRetry(() => import("./modules/projekte/pages/ProjekteLadenbauListePage.jsx"));

/* Projekte – Neu/Details/Bearbeiten — lazy */
const ProjektErstellen = lazyWithRetry(() => import("./modules/projekte/pages/ProjektCreatePage.jsx"));
const ProjektElektroDetails = lazyWithRetry(() => import("./modules/projekte/pages/ProjektElektroDetailsPage.jsx"));
const ProjektPVDetails = lazyWithRetry(() => import("./modules/projekte/pages/ProjektPVDetailsPage.jsx"));
const ProjektSicherheitDetails = lazyWithRetry(() => import("./modules/projekte/pages/ProjektSicherheitDetailsPage.jsx"));
const ProjektLadestationDetails = lazyWithRetry(() => import("./modules/projekte/pages/ProjektLadestationDetailsPage.jsx"));
const ProjektInternDetails = lazyWithRetry(() => import("./modules/projekte/pages/ProjektInternDetailsPage.jsx"));
const ProjektLadenbauDetails = lazyWithRetry(() => import("./modules/projekte/pages/ProjektLadenbauDetailsPage.jsx"));
const ProjektBearbeiten = lazyWithRetry(() => import("./modules/projekte/pages/ProjektEditPage.jsx"));

/* Service — lazy */
const ServiceListe = lazyWithRetry(() => import("./components/service/ServiceListe"));
const ServiceDetail = lazyWithRetry(() => import("./pages/ServiceDetail"));
const ServiceErstellen = lazyWithRetry(() => import("./components/service/ServiceErstellen"));
const ServiceBearbeiten = lazyWithRetry(() => import("./components/service/ServiceBearbeiten"));


/* Offerten — eager imports entfernt (siehe lazy oben) */

/* Mängelberichte — lazy */
const MaengelListe = lazyWithRetry(() => import("./components/Maengel/Liste.jsx"));
const MaengelNeu = lazyWithRetry(() => import("./components/Maengel/Erstellen.jsx"));
const MaengelDetail = lazyWithRetry(() => import("./pages/MaengelDetail.jsx"));
const MaengelBearbeiten = lazyWithRetry(() => import("./components/Maengel/MaengelBearbeiten.jsx"));
const MaengelOriginal = lazyWithRetry(() => import("./components/Maengel/MaengelOriginal.jsx"));

/* Profil */
import Profil from "./pages/Profil.jsx";
import NotificationsPage from "./components/notifications/Notifications.jsx";

/* Übersicht & Monitoring — lazy */
const LeitstandPage = lazyWithRetry(() => import("./pages/Übersicht.jsx"));
const Monitoring = lazyWithRetry(() => import("./pages/Monitoring.jsx"));
const MeineAufgaben = lazyWithRetry(() => import("./pages/MeineAufgaben.jsx"));

/* Inventar */
import InventarListe from "./pages/Inventar.jsx";
import InventarDetail from "./components/Inventar/InventarDetail.jsx";
import InventarErstellen from "./components/Inventar/InventarErstellen"
import InventarBearbeiten from "./components/Inventar/InventarBearbeiten.jsx";


/* Hilfe — lazy */
const Hilfe = lazyWithRetry(() => import("./pages/Hilfe.jsx"));
const AcademyPage = lazyWithRetry(() => import("./pages/AcademyPage.jsx"));

/* Product Comms System */
import ReleaseDetails from "./pages/system/ReleaseDetails.jsx";
import UiGuardrails from "./pages/UiGuardrails.jsx";
import ReleaseModal from "./components/system/ReleaseModal.jsx";
import OnboardingModal from "./components/system/OnboardingModal.jsx";
import AcademyOnboardingBanner from "./components/academy/AcademyOnboardingBanner.jsx";
import WartungsvertraegePage from "./pages/Wartungsvertraege.jsx";
import WartungsvertragDetail from "./pages/WartungsvertragDetail.jsx";
import WartungsvertragErstellen from "./pages/WartungsvertragErstellen.jsx";

/* Papierkorb (Admin) */
import AdminPapierkorb from "./pages/AdminPapierkorb.jsx";

/* Error Pages */
import { NotFoundPage } from "./pages/ErrorPage.jsx";



function LegacyHelpArticleRedirect() {
  const { slug } = useParams();
  const target = slug
    ? `/hilfe?tab=center&helpSlug=${encodeURIComponent(slug)}`
    : "/hilfe?tab=center";
  return <Navigate to={target} replace />;
}

function LazyFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-slate-400 text-sm">
      Lade Modul…
    </div>
  );
}

function AppRoutes() {
  const NON_STAFF_ALLOWED = ["admin", "geschaeftsleitung", "geschäftsleitung", "gl", "projektleitung", "pl", "sek", "sekretariat"];
  const InventarRouteBoundary = ({ children }) => (
    <ErrorBoundary name="Inventar">
      {children}
    </ErrorBoundary>
  );

  return (
    <Routes>
      {/* Öffentlich */}
      <Route path="/login" element={<Login />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/ui/guardrails" element={<UiGuardrails />} />

      {/* Pflicht-Schritte */}
      <Route path="/2fa/setup" element={<TwoFASetup />} />
      <Route path="/first-password" element={<FirstPassword />} />
      <Route
        path="/academy"
        element={
          <PrivateRoute>
            <AcademyPage />
          </PrivateRoute>
        }
      />

      {/* Dashboard */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <AdminPage />
          </PrivateRoute>
        }
      />
       {/* Admin – Benutzer-Details / Neu */}
      <Route
        path="/admin/users/new"
        element={
          <PrivateRoute>
            <AdminUserEditPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <PrivateRoute>
            <AdminUserEditPage />
          </PrivateRoute>
        }
      />

      {/* Admin – Papierkorb */}
      <Route
        path="/admin/papierkorb"
        element={
          <PrivateRoute allowed={["admin"]}>
            <AdminPapierkorb />
          </PrivateRoute>
        }
      />

      {/* Kunden — Liste/Details für alle Rollen; Stammdatenpflege nur Power-User */}
      <Route path="/kunden" element={<PrivateRoute><KundenListe /></PrivateRoute>} />
      <Route path="/kunden/neu" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><KundenNeu /></PrivateRoute>} />
      <Route path="/kunden/:id" element={<PrivateRoute><KundenDetails /></PrivateRoute>} />
      <Route path="/kunden/:id/bearbeiten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><KundenBearbeiten /></PrivateRoute>} />

      {/* Objekte (Liegenschaften) — Details für alle Rollen */}
      <Route path="/objekte/:id" element={<PrivateRoute><ObjektDetails /></PrivateRoute>} />

      {/* Projekte – Listen */}
      <Route path="/projekte/elektro" element={<PrivateRoute><ProjektElektroListe /></PrivateRoute>} />
      <Route path="/projekte/pv" element={<PrivateRoute><ProjektePVListe /></PrivateRoute>} />
      <Route path="/projekte/sicherheit" element={<PrivateRoute><ProjekteSicherheitListe /></PrivateRoute>} />
      <Route path="/projekte/ladestation" element={<PrivateRoute><ProjekteLadestationListe /></PrivateRoute>} />
      <Route path="/projekte/intern" element={<PrivateRoute><ProjekteInternListe /></PrivateRoute>} />
      <Route path="/projekte/ladenbau" element={<PrivateRoute><ProjekteLadenbauListe /></PrivateRoute>} />

      {/* Projekte – Neu/Details/Bearbeiten */}
      <Route path="/projekte/:typ/neu" element={<PrivateRoute><ProjektErstellen /></PrivateRoute>} />
      <Route path="/projekte/elektro/:id" element={<PrivateRoute><ProjektElektroDetails /></PrivateRoute>} />
      <Route path="/projekte/pv/:id" element={<PrivateRoute><ProjektPVDetails /></PrivateRoute>} />
      <Route path="/projekte/sicherheit/:id" element={<PrivateRoute><ProjektSicherheitDetails /></PrivateRoute>} />
      <Route path="/projekte/ladestation/:id" element={<PrivateRoute><ProjektLadestationDetails /></PrivateRoute>} />
      <Route path="/projekte/intern/:id" element={<PrivateRoute><ProjektInternDetails /></PrivateRoute>} />
      <Route path="/projekte/ladenbau/:id" element={<PrivateRoute><ProjektLadenbauDetails /></PrivateRoute>} />
      <Route path="/projekte/:typ/:id/bearbeiten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><ProjektBearbeiten /></PrivateRoute>} />

      {/* Offerten */}
      <Route
        path="/offerten"
        element={
          <PrivateRoute allowed={NON_STAFF_ALLOWED}>
            <OffertenListe />
          </PrivateRoute>
        }
      />
      <Route
        path="/offerten/neu"
        element={
          <PrivateRoute allowed={NON_STAFF_ALLOWED}>
            <OfferteNeu />
          </PrivateRoute>
        }
      />
      <Route
        path="/offerten/:id"
        element={
          <PrivateRoute allowed={NON_STAFF_ALLOWED}>
            <OfferteDetails />
          </PrivateRoute>
        }
      />
      <Route
       path="/offerten/:id/bearbeiten"
        element={
          <PrivateRoute allowed={NON_STAFF_ALLOWED}>
            <OfferteBearbeiten />
          </PrivateRoute>
        }
    />

      {/* Sales-Dokumente (Feature-Flags + Power-Rollen) */}
      <Route path="/verkauf" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufHome /></PrivateRoute>} />
      <Route path="/verkauf/offerten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufOffertenPage /></PrivateRoute>} />
      <Route path="/verkauf/offerten/neu" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufOfferCreate /></PrivateRoute>} />
      <Route path="/verkauf/offerten/erstellen" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufOfferWiretrackCreate /></PrivateRoute>} />
      <Route path="/verkauf/ki" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufAiPage /></PrivateRoute>} />
      <Route path="/verkauf/wissensbasis" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufKnowledgePage /></PrivateRoute>} />
      <Route path="/verkauf/vorlagen" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufTemplatesPage /></PrivateRoute>} />
      <Route path="/verkauf/sharepoint" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufSharepointPage /></PrivateRoute>} />
      <Route path="/rechnungen" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentList kind="invoice" /></PrivateRoute>} />
      <Route path="/rechnungen/neu" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><VerkaufInvoiceCreate /></PrivateRoute>} />
      <Route path="/rechnungen/erstellen" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentEditor mode="create" kind="invoice" /></PrivateRoute>} />
      <Route path="/rechnungen/:id" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentEditor mode="edit" kind="invoice" /></PrivateRoute>} />
      <Route path="/rechnungen/:id/bearbeiten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentEditor mode="edit" kind="invoice" /></PrivateRoute>} />
      <Route path="/offerten-sales" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentList kind="offer" /></PrivateRoute>} />
      <Route path="/offerten-sales/neu" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentEditor mode="create" kind="offer" /></PrivateRoute>} />
      <Route path="/offerten-sales/:id" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentEditor mode="edit" kind="offer" /></PrivateRoute>} />
      <Route path="/offerten-sales/:id/bearbeiten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesDocumentEditor mode="edit" kind="offer" /></PrivateRoute>} />
      <Route path="/debitoren" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><DebitorenPage /></PrivateRoute>} />
      <Route path="/verkauf/einstellungen" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><SalesSettingsPage /></PrivateRoute>} />

      {/* Service */}
      <Route path="/service" element={<PrivateRoute><ServiceListe /></PrivateRoute>} />
      <Route path="/service/neu" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><ServiceErstellen /></PrivateRoute>} />
      <Route path="/service/:id" element={<PrivateRoute><ServiceDetail /></PrivateRoute>} />
      <Route path="/service/:id/bearbeiten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><ServiceBearbeiten /></PrivateRoute>} />
      <Route path="/wartungsvertraege" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><WartungsvertraegePage /></PrivateRoute>} />
      <Route path="/wartungsvertraege/neu" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><WartungsvertragErstellen /></PrivateRoute>} />
      <Route path="/wartungsvertraege/:id/bearbeiten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><WartungsvertragErstellen /></PrivateRoute>} />
      <Route path="/wartungsvertraege/:id" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><WartungsvertragDetail /></PrivateRoute>} />

      {/* Archiv */}
      <Route path="/archiv" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><Archiv /></PrivateRoute>} />

      {/* Mängelberichte */}
      <Route path="/maengel" element={<PrivateRoute><MaengelListe /></PrivateRoute>} />
      <Route path="/maengel/neu" element={<PrivateRoute><MaengelNeu /></PrivateRoute>} />
      <Route path="/maengel/:id" element={<PrivateRoute><MaengelDetail /></PrivateRoute>} />
      <Route path="/maengel/:id/bearbeiten" element={<PrivateRoute><MaengelBearbeiten /></PrivateRoute>} />
      <Route path="/maengel/:id/original" element={<PrivateRoute><MaengelOriginal /></PrivateRoute>} />

      {/* Profil */}
      <Route path="/profil" element={<PrivateRoute><Profil /></PrivateRoute>} />
      <Route path="/benachrichtigungen" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />

      {/* Monitoring */}
      <Route path="/monitoring" element={<Navigate to="/monitoring/alle" replace />} />
      <Route path="/monitoring/:typ" element={<PrivateRoute><Monitoring /></PrivateRoute>} />
      <Route path="/meine-aufgaben" element={<PrivateRoute><MeineAufgaben /></PrivateRoute>} />

      {/* Inventar */}
      <Route path="/inventar" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><InventarRouteBoundary><InventarListe /></InventarRouteBoundary></PrivateRoute>} />
      <Route path="/inventar/neu" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><InventarRouteBoundary><InventarErstellen /></InventarRouteBoundary></PrivateRoute>} />
      <Route path="/inventar/:id" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><InventarRouteBoundary><InventarDetail /></InventarRouteBoundary></PrivateRoute>} />
      <Route path="/inventar/:id/bearbeiten" element={<PrivateRoute allowed={NON_STAFF_ALLOWED}><InventarRouteBoundary><InventarBearbeiten /></InventarRouteBoundary></PrivateRoute>} />

    {/* Kalender — /kalender/screen: Kiosk/Bildschirm (ohne PrivateRoute); API braucht Session */}
      <Route path="/kalender" element={<PrivateRoute><Kalender /></PrivateRoute>} />
      <Route path="/kalender/screen" element={<KalenderScreen />} />

{/* Hilfe (Legacy) */}
      <Route path="/hilfe" element={<PrivateRoute><Hilfe /></PrivateRoute>} />

      {/* Product Comms System */}
      <Route path="/releases/:version" element={<PrivateRoute><ReleaseDetails /></PrivateRoute>} />
      <Route path="/hilfe-center" element={<PrivateRoute><Navigate to="/hilfe?tab=center" replace /></PrivateRoute>} />
      <Route path="/hilfe-center/:slug" element={<PrivateRoute><LegacyHelpArticleRedirect /></PrivateRoute>} />
      {/* Leitstand */}
      <Route path="/leitstand" element={<Navigate to="/leitstand/elektro" replace />} />
      <Route
        path="/leitstand/:typ"
        element={
          <PrivateRoute allowed={["admin","geschaeftsleitung","geschäftsleitung","gl","projektleitung","pl","sek","sekretariat"]}>
            <LeitstandPage />
          </PrivateRoute>
        }
      />

      {/* 404 Fallback */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  // 🔁 Aktivitäts-Ping global anschalten
  useSessionActivity();

  return (
    <ThemeProvider>
      <RadioProvider>
        <ErrorBoundary>
          <ConfirmProvider>
            <ToastSystem />
            <PdfExportPreviewHost />
            <div id="app" className="kill-page-gradient">
              <BrowserRouter>
                <ScrollManager />
                <AuthRequiredListener />
              <ErrorBoundary>
                <ReleaseModal />
                <OnboardingModal />
                <AcademyOnboardingBanner />
                <Suspense fallback={<LazyFallback />}>
                  <AppRoutes />
                </Suspense>
              </ErrorBoundary>
            </BrowserRouter>
          </div>
        </ConfirmProvider>
      </ErrorBoundary>
    </RadioProvider>
  </ThemeProvider>
  );
}
